import type { ReactNode } from "react";
import {
  useController,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import FormFieldShell from "./FormFieldShell";

export interface CheckboxFieldProps<TFieldValues extends FieldValues = any> {
  className?: string;
  control?: Control<TFieldValues>;
  disabled?: boolean;
  errorClassName?: string;
  id?: string;
  label?: ReactNode;
  labelClassName?: string;
  name?: Path<TFieldValues>;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  onValueChange?: (checked: boolean) => void;
  required?: boolean;
  wrapperClassName?: string;
}

interface CheckboxBaseProps {
  className?: string;
  disabled?: boolean;
  errorClassName?: string;
  errorMessage?: string;
  id?: string;
  label?: ReactNode;
  labelClassName?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  required?: boolean;
  shouldShowError?: boolean;
  wrapperClassName?: string;
}

function CheckboxBase({
  className,
  disabled,
  errorClassName,
  errorMessage,
  id,
  label,
  labelClassName,
  checked = false,
  onChange,
  required,
  shouldShowError = false,
  wrapperClassName = "w-full",
}: Readonly<CheckboxBaseProps>) {
  const inputId = id ?? (typeof label === "string" ? label : undefined);

  return (
    <FormFieldShell
      error={shouldShowError ? errorMessage : undefined}
      errorClassName={errorClassName}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <label
        htmlFor={inputId}
        className={cn(
          "inline-flex items-center gap-2.5 cursor-pointer select-none group",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <div className="relative flex items-center justify-center">
          <input
            type="checkbox"
            id={inputId}
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
            className="sr-only"
          />
          <div
            className={cn(
              "w-4 h-4 rounded border transition-all flex items-center justify-center",
              checked
                ? "bg-purple-600 border-purple-600 text-white"
                : "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 group-hover:border-purple-400",
              shouldShowError && "border-red-500 dark:border-red-500",
              className
            )}
          >
            {checked && <Check className="w-3 h-3 stroke-[3]" />}
          </div>
        </div>
        {label ? (
          <span
            className={cn(
              "text-xs font-semibold text-slate-700 dark:text-slate-300",
              labelClassName
            )}
          >
            {label}
            {required ? <span className="text-red-500 ml-1 font-bold">*</span> : null}
          </span>
        ) : null}
      </label>
    </FormFieldShell>
  );
}

function CheckboxControlled<TFieldValues extends FieldValues>({
  control,
  name,
  onValueChange,
  ...rest
}: Readonly<
  CheckboxFieldProps<TFieldValues> & {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
  }
>) {
  const { field, fieldState, formState } = useController({
    control,
    name,
  });

  const shouldShowError =
    !!(fieldState.isTouched || formState.isSubmitted) && !!fieldState.error;
  const isChecked = !!field.value;

  return (
    <CheckboxBase
      {...rest}
      id={rest.id ?? name}
      checked={isChecked}
      shouldShowError={shouldShowError}
      errorMessage={fieldState.error?.message}
      onChange={(checked) => {
        field.onChange(checked);
        onValueChange?.(checked);
      }}
    />
  );
}

export function CheckboxField<TFieldValues extends FieldValues = any>(
  props: Readonly<CheckboxFieldProps<TFieldValues>>
) {
  if (props.control && props.name) {
    return (
      <CheckboxControlled
        {...props}
        control={props.control}
        name={props.name}
      />
    );
  }
  return (
    <CheckboxBase
      {...props}
      checked={props.checked}
      onChange={(val) => {
        props.onChange?.(val);
        props.onValueChange?.(val);
      }}
    />
  );
}

export default CheckboxField;
